import React, { useEffect, useState } from "react";
import { BotonVolver } from "@/components/BotonVolver";
import { hayDialogoInstalacion, onCambioInstalacion, lanzarInstalacion } from "@/lib/instalar";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { isIOS, isAndroid, isStandalone, isIOSNoSafari, iosVersion, pushSupported, permiso, activarAvisos, probarAviso } from "@/lib/push";
import { IlusCompartir, IlusMenu, IlusAnadir, IlusIcono, IlusAndroidMenu } from "@/components/push/Ilustraciones";
import { ComprobarAvisos } from "@/components/push/ComprobarAvisos";

const Card = ({ children, testid, className = "" }) => (
  <section data-testid={testid} className={`bg-white border border-[#E5DFD5] rounded-2xl p-5 ${className}`}>{children}</section>
);
const Paso = ({ n, titulo, children }) => (
  <div className="py-4 border-b border-[#F2EDE4] last:border-0" data-testid={`paso-${n}`}>
    <p className="font-heading text-lg font-bold text-[#1C2421] flex items-start gap-3">
      <span className="w-8 h-8 rounded-full bg-[#3A6B56] text-white flex items-center justify-center text-base shrink-0">{n}</span>{titulo}
    </p>
    {children && <div className="mt-3">{children}</div>}
  </div>
);
const btn = "w-full min-h-[56px] rounded-xl font-semibold text-lg flex items-center justify-center gap-2";

const CopiarEnlace = () => (
  <div className="mt-3">
    <button data-testid="copiar-enlace" onClick={async () => {
      try { await navigator.clipboard.writeText(window.location.origin + "/avisos-movil"); toast.success("Enlace copiado"); }
      catch { toast.error("No se pudo copiar. Mantén pulsada la dirección de arriba y copia."); }
    }} className={`${btn} bg-white border-2 border-[#3A6B56] text-[#2E5645]`}>
      <Icons.Copy size={20} /> Copiar enlace
    </button>
    <p className="text-sm text-[#6E7A74] text-center mt-1.5">Después pégalo en Safari.</p>
  </div>
);

// Instalación en iPhone (Safari) con pasos visuales.
const GuiaIOS = () => {
  const [hecho, setHecho] = useState(false);
  if (isIOSNoSafari()) {
    return (
      <Card testid="ios-no-safari">
        <Icons.Compass size={32} className="text-[#007AFF]" />
        <p className="font-heading text-xl font-bold text-[#1C2421] mt-2">Para instalar AL DÍA en iPhone, abre esta página en Safari.</p>
        <CopiarEnlace />
      </Card>
    );
  }
  if (hecho) {
    return (
      <Card testid="ios-ya-instalada">
        <IlusIcono />
        <p className="font-heading text-xl font-bold text-[#1C2421] mt-4">Abre AL DÍA desde el nuevo icono.</p>
        <p className="text-base text-[#4A5550] mt-2">Es posible que tengas que iniciar sesión de nuevo; es normal porque Safari y la app instalada pueden mantener sesiones separadas.</p>
        <p className="text-base text-[#4A5550] mt-2">Al entrar, AL DÍA te guiará para activar los avisos.</p>
        <button onClick={() => setHecho(false)} className="mt-4 text-sm font-semibold text-[#3A6B56]" data-testid="ios-ver-pasos">Ver los pasos otra vez</button>
      </Card>
    );
  }
  return (
    <Card testid="ios-guia">
      <p className="font-heading text-xl font-bold text-[#1C2421]">Para instalar AL DÍA en tu iPhone:</p>
      <p className="text-sm text-[#6E7A74] mt-1">Las imágenes son orientativas; tu pantalla puede verse algo distinta.</p>
      <Paso n={1} titulo="Abre AL DÍA en Safari." />
      <Paso n={2} titulo="Pulsa el botón Compartir (el cuadrado con la flecha hacia arriba).">
        <IlusCompartir />
        <p className="text-sm text-[#6E7A74] mt-2">Si no lo ves, toca «•••» en la barra de abajo y después «Compartir».</p>
      </Paso>
      <Paso n={3} titulo="Elige «Añadir a pantalla de inicio»."><IlusMenu /><p className="text-sm text-[#6E7A74] mt-2">Si no aparece, desliza la lista hacia arriba.</p></Paso>
      <Paso n={4} titulo="Confirma pulsando «Añadir»."><IlusAnadir /></Paso>
      <Paso n={5} titulo="Abre AL DÍA desde el nuevo icono."><IlusIcono /></Paso>
      <Paso n={6} titulo="Cuando aparezca el aviso, acepta «Permitir» notificaciones." />
      <button data-testid="ios-ya-la-he-instalado" onClick={() => setHecho(true)} className={`${btn} bg-[#3A6B56] text-white mt-4`}>
        <Icons.Check size={22} /> Ya la he instalado
      </button>
      <p className="text-sm text-[#6E7A74] mt-4">¿Estás en otra app (WhatsApp, Mail…) y no ves «Añadir a pantalla de inicio»? Ábrela en Safari:</p>
      <CopiarEnlace />
    </Card>
  );
};

const GuiaAndroidInstalar = () => {
  const [prompt, setPrompt] = useState(hayDialogoInstalacion);
  useEffect(() => onCambioInstalacion(() => setPrompt(hayDialogoInstalacion())), []);
  const instalar = async () => { const r = await lanzarInstalacion(); if (r === "aceptada") toast.success("AL DÍA Fácil se está instalando."); else setPrompt(false); };
  return (
    <Card testid="android-instalar">
      <p className="font-heading text-lg font-bold text-[#1C2421]">Recomendado: instala AL DÍA en tu móvil</p>
      <p className="text-sm text-[#6E7A74] mt-1">Así tendrás un icono como cualquier otra app. Los avisos funcionan también sin instalarla.</p>
      {prompt ? (
        <button data-testid="android-instalar-btn" onClick={instalar}
          className={`${btn} bg-[#3A6B56] text-white mt-3`}><Icons.Download size={20} /> Instalar AL DÍA</button>
      ) : (
        <div className="mt-3"><p className="text-base text-[#1C2421] mb-2">En Chrome, pulsa los tres puntos <b>⋮</b> y elige «Instalar aplicación» o «Añadir a pantalla de inicio».</p><IlusAndroidMenu /></div>
      )}
    </Card>
  );
};

// Paso de activación (app instalada en iPhone, o Android/ordenador).
const Activar = ({ onActivado }) => {
  const [estado, setEstado] = useState(permiso());
  const [busy, setBusy] = useState(false);
  const [listo, setListo] = useState(false);
  const [prueba, setPrueba] = useState(false);
  const ios = isIOS();
  const activar = async () => {
    setBusy(true);
    try {
      const r = await activarAvisos();
      setEstado(r.perm);
      if (r.ok) { setListo(true); onActivado && onActivado(); toast.success("Avisos activados en este dispositivo"); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron activar los avisos. Inténtalo de nuevo.");
    } finally { setBusy(false); }
  };
  return (
    <Card testid="activar-avisos">
      <p className="font-heading text-xl font-bold text-[#1C2421]">Activa los avisos</p>
      <p className="text-base text-[#4A5550] mt-1">Activa las notificaciones para que AL DÍA pueda avisarte aunque no la tengas abierta.</p>
      {estado === "denied" ? (
        <p data-testid="avisos-denegados" className="mt-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-xl p-4 text-base text-[#92400E]">
          {ios ? "Puedes activarlo más adelante desde Ajustes > Notificaciones > AL DÍA." : "Puedes activarlo más adelante desde los ajustes del teléfono: Notificaciones de AL DÍA (o de Chrome)."}
        </p>
      ) : !listo ? (
        <>
          <button data-testid="activar-avisos-btn" onClick={activar} disabled={busy} className={`${btn} bg-[#3A6B56] text-white mt-4 disabled:opacity-60`}>
            {busy ? <Icons.Loader2 size={22} className="animate-spin" /> : <Icons.Bell size={22} />} Activar avisos
          </button>
          <p className="text-base text-[#4A5550] mt-3">
            {ios ? "iPhone te pedirá permiso para enviarte notificaciones." : "El teléfono te pedirá permiso para enviarte notificaciones."} Pulsa «Permitir» para que AL DÍA pueda avisarte de citas, planes y otros asuntos importantes.
          </p>
          {ios && <p className="text-sm text-[#6E7A74] mt-1">Apple exige que este permiso lo aceptes tú manualmente.</p>}
        </>
      ) : (
        <div className="mt-4 space-y-3" data-testid="avisos-activados">
          <p className="text-base font-semibold text-[#166534] flex items-center gap-2"><Icons.CheckCircle2 size={20} /> Avisos activados en este dispositivo.</p>
          {!prueba ? (
            <button data-testid="probar-aviso-btn" onClick={async () => {
              try { await probarAviso(); setPrueba(true); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar el aviso."); }
            }} className={`${btn} bg-[#1C2421] text-white`}><Icons.BellRing size={20} /> Probar aviso</button>
          ) : (
            <p data-testid="probar-aviso-enviado" className="bg-[#F6F3EE] rounded-xl p-4 text-base text-[#1C2421]">
              Aviso enviado. En unos segundos debería llegarte «AL DÍA: aviso de prueba». Confírmalo abajo con «Comprobar instalación y avisos».
            </p>
          )}
        </div>
      )}
      {!ios && <p className="text-sm text-[#6E7A74] mt-4">En algunos móviles Android (Xiaomi, Huawei, Samsung…) conviene quitar el ahorro de batería para AL DÍA o Chrome, para que los avisos no se retrasen.</p>}
    </Card>
  );
};

export default function AvisosMovil() {
  const ios = isIOS();
  const v = iosVersion();
  const necesitaInstalar = ios && !isStandalone();
  const [, refresh] = useState(0);
  const pruebaOk = new URLSearchParams(window.location.search).get("prueba") === "ok";

  return (
    <div className="space-y-5" data-testid="avisos-movil-page">
      <BotonVolver porDefecto="/perfil" testid="avisos-movil-volver" />
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center"><Icons.Smartphone size={26} className="text-[#3A6B56]" /></div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">Avisos en el móvil</h1>
          <p className="text-base text-[#6E7A74]">Te guiamos paso a paso.</p>
        </div>
      </header>
      {pruebaOk && <p data-testid="prueba-ok-banner" className="bg-[#DCFCE7] text-[#166534] rounded-xl p-4 font-semibold">Has abierto el aviso de prueba: ¡funciona!</p>}

      {ios && v && v < 16.4 ? (
        <Card testid="ios-antiguo">
          <p className="font-heading text-lg font-bold text-[#1C2421]">Tu iPhone necesita una versión más reciente</p>
          <p className="text-base text-[#4A5550] mt-1">Los avisos requieren iOS 16.4 o posterior. Puedes actualizar en Ajustes &gt; General &gt; Actualización de software.</p>
        </Card>
      ) : necesitaInstalar ? <GuiaIOS /> : !pushSupported() ? (
        <Card testid="push-no-soportado">
          <p className="text-base text-[#4A5550]">Este navegador no permite avisos. En Android usa Chrome; en iPhone, Safari y la app instalada.</p>
        </Card>
      ) : (
        <>
          {isAndroid() && !isStandalone() && <GuiaAndroidInstalar />}
          <Activar onActivado={() => refresh((n) => n + 1)} />
        </>
      )}

      <ComprobarAvisos />

      <p className="text-sm text-[#6E7A74] flex gap-2"><Icons.ShieldCheck size={16} className="shrink-0 text-[#3A6B56] mt-0.5" />
        Los avisos son discretos. En la pantalla bloqueada solo mostramos la información necesaria. Los detalles completos se ven al abrir AL DÍA.
      </p>
    </div>
  );
}
